import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Award, ShieldCheck, Gem, Handshake } from "lucide-react";
import SmoothScroll from "@/components/site/SmoothScroll";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import { Reveal, FadeUp } from "@/components/site/Reveal";
import { CONTACT, PROCESS } from "@/data/content";

const VALUES = [
  {
    icon: Gem,
    title: "Craftsmanship first",
    body: "Every tile — from a humble subway to a book-matched marble slab — is set with the same European-standard precision. If it isn't perfect, it isn't finished.",
  },
  {
    icon: ShieldCheck,
    title: "Built to last",
    body: "Correct preparation and AS 3740 compliant waterproofing beneath every wet area. The work you never see is the work that matters most.",
  },
  {
    icon: Award,
    title: "Honest advice",
    body: "We tell you what will genuinely perform in your space — not what's easiest to sell. Detailed written quotes, no surprises, no shortcuts.",
  },
  {
    icon: Handshake,
    title: "Respect for your home",
    body: "Protected floors, tidy sites and clear communication from first visit to final handover. We treat your home like it's our own.",
  },
];

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const title = "About Perth Tiling WA | Perth's Complete Tiling Specialists";
  const description = "Meet Perth Tiling WA — a Perth tiling team built on European-standard craftsmanship, honest advice and finishes that last. Bathrooms, kitchens, outdoor and commercial tiling across Perth.";
  const keywords = "about Perth Tiling WA, Perth tilers, tiling company Perth, European standard tiling, master tiler Perth";
  const breadcrumb = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
  ];

  return (
    <SmoothScroll>
      <Seo pageKey="about" overrideTitle={title} overrideDescription={description} overrideKeywords={keywords} breadcrumb={breadcrumb} />
      <div className="grain-overlay" aria-hidden />
      <div data-testid="about-page" className="bg-[#f8fafc] min-h-screen">
        <Navbar />

        {/* Hero */}
        <section className="relative pt-40 pb-20 md:pt-52 md:pb-28 border-b border-[#0f172a]/10">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12">
            <nav className="mb-8 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#64748b]" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-[#9c7a4d] transition-colors">Home</Link>
              <span>/</span>
              <span className="text-[#0f172a]">About</span>
            </nav>
            <p className="overline !text-[#9c7a4d] mb-5">About Us</p>
            <h1 className="font-serif font-light text-[#0f172a] text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
              <Reveal load>The standard</Reveal>
              <Reveal load delay={0.12}>behind the surface.</Reveal>
            </h1>
            <FadeUp delay={0.2}>
              <p className="mt-8 max-w-2xl text-[#475569] text-lg md:text-xl font-light leading-relaxed">
                Perth Tiling WA exists for one reason: to bring genuinely complete tiling craftsmanship to Perth's finest homes and commercial spaces. Every aspect of tiling, one uncompromising standard.
              </p>
            </FadeUp>
          </div>
        </section>

        {/* Story */}
        <section className="py-20 md:py-28">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-5">
              <p className="overline !text-[#9c7a4d] mb-6">Our Story</p>
              <h2 className="font-serif font-light text-[#0f172a] text-3xl md:text-5xl leading-tight tracking-tight">
                <Reveal>Precision tiling,</Reveal>
                <Reveal delay={0.08}>the Perth way.</Reveal>
              </h2>
            </div>
            <div className="lg:col-span-7">
              <FadeUp>
                <p className="text-[#475569] text-lg md:text-xl font-light leading-relaxed mb-6">
                  We started Perth Tiling WA with a simple frustration: too many beautiful Perth homes let down by rushed, careless tiling. Lippage you can feel underfoot. Waterproofing done on hope. Setout that fights the architecture instead of flattering it.
                </p>
                <p className="text-[#475569] text-lg md:text-xl font-light leading-relaxed mb-6">
                  So we built a team that does it properly. From a single splashback to a full commercial fit-out, we install every aspect of tiling — ceramic, porcelain, mosaic, large-format and natural stone — with the preparation, setout and detailing that separates a good job from a lasting one.
                </p>
                <p className="text-[#475569] text-lg md:text-xl font-light leading-relaxed">
                  Today we work across Perth's premier suburbs, alongside our collaboration partners Modify and Exclusive Tiling &amp; Stone, delivering everything from heritage bathroom restorations in Fremantle to large-format architectural builds on the coast.
                </p>
              </FadeUp>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 md:py-28 bg-[#f1f5f9] border-y border-[#0f172a]/10">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12">
            <p className="overline !text-[#9c7a4d] mb-6">What We Stand For</p>
            <h2 className="font-serif font-light text-[#0f172a] text-3xl md:text-5xl leading-tight tracking-tight mb-14">
              <Reveal>Four promises,</Reveal>
              <Reveal delay={0.08}>kept on every job.</Reveal>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {VALUES.map((v, i) => (
                <FadeUp key={v.title} delay={i * 0.08}>
                  <div data-testid={`about-value-${i}`} className="border border-[#0f172a]/10 bg-white p-8 h-full">
                    <v.icon className="h-6 w-6 text-[#9c7a4d] mb-6" strokeWidth={1.5} />
                    <h3 className="font-serif text-2xl text-[#0f172a] mb-3">{v.title}</h3>
                    <p className="text-[#64748b] font-light leading-relaxed">{v.body}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        {/* Process recap */}
        <section className="py-20 md:py-28">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12">
            <p className="overline !text-[#9c7a4d] mb-6">How We Work</p>
            <h2 className="font-serif font-light text-[#0f172a] text-3xl md:text-5xl leading-tight tracking-tight mb-14">
              <Reveal>A method,</Reveal>
              <Reveal delay={0.08}>not a gamble.</Reveal>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {PROCESS.map((p, i) => (
                <FadeUp key={p.n} delay={i * 0.08}>
                  <div data-testid={`about-process-${i}`} className="border-t-2 border-[#9c7a4d] pt-6">
                    <span className="text-[#9c7a4d] text-xs uppercase tracking-[0.22em]">{p.n}</span>
                    <h3 className="font-serif text-2xl text-[#0f172a] mt-3 mb-3">{p.title}</h3>
                    <p className="text-[#64748b] font-light leading-relaxed">{p.body}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.2}>
              <div className="mt-16 flex flex-wrap gap-4">
                <Link to="/services" data-testid="about-services-cta" className="group inline-flex items-center gap-3 bg-[#9c7a4d] text-white hover:bg-[#0f172a] px-8 py-4 text-xs uppercase tracking-[0.22em] transition-colors">
                  Explore Our Services <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a href={`tel:${CONTACT.phoneRaw}`} className="inline-flex items-center gap-3 border border-[#0f172a]/20 text-[#0f172a] hover:border-[#9c7a4d] hover:text-[#9c7a4d] px-8 py-4 text-xs uppercase tracking-[0.22em] transition-colors">
                  <Phone className="h-4 w-4" /> {CONTACT.phone}
                </a>
              </div>
            </FadeUp>
          </div>
        </section>

        <Footer />
      </div>
    </SmoothScroll>
  );
}
